export type HapticPreset = {
  name: string;
  description: string;
  frequencies: number[]; // Base frequencies for haptic output, can be mapped or modulated
  intensity: number; // Overall intensity multiplier for this preset
  duration: number; // Default duration for a haptic effect in ms

  // New fields for advanced customization:
  latencyCompensation?: number; // Audio playback latency compensation in ms (e.g., 0 or 20)
  fftSize?: number; // FFT size for audio analysis (e.g., 2048, 4096)
  hopSize?: number; // Hop size for audio analysis (e.g., 512, 1024) - affects temporal resolution
  targetFrequencyBand?: { min: number; max: number }; // Target frequency band for beat detection (e.g., { min: 60, max: 120 } for bass)
  onsetSensitivity?: number; // Sensitivity for onset detection. Replaces energyThreshold.
                           // Used as 'X' in (1+X) multiplier for previous energy. Higher is less sensitive. (e.g. 0.4 means 1.4x)
  timingWindow?: number; // Window in seconds for matching feedback points to current time (e.g., 0.05)
  
  icon: string; // Emoji or icon character for UI representation
};

export type TrackAnalysis = {
  trackId: string;
  energy: number;
  feedbackPoints: FeedbackPoint[];
  timestamp: number;
};

export type FeedbackPoint = {
  timestamp: number;
  intensity: number;
  frequency: number;
};

export const DEFAULT_PRESETS: Record<string, HapticPreset> = {
  pop: {
    name: "Pop",
    description: "Highlight rhythmic elements and vocals",
    frequencies: [60, 120, 240],
    onsetSensitivity: 0.4, // Was energyThreshold
    intensity: 0.7,
    duration: 50,
    icon: "🎵",
    latencyCompensation: 0,
    fftSize: 2048,
    hopSize: 512,
    targetFrequencyBand: { min: 60, max: 120 },
    timingWindow: 0.05,
  },
  rock: {
    name: "Rock",
    description: "Emphasize guitars and drums",
    frequencies: [40, 100, 200],
    onsetSensitivity: 0.5, // Was energyThreshold
    intensity: 0.8,
    duration: 80,
    icon: "🎸",
    latencyCompensation: 0,
    fftSize: 2048,
    hopSize: 512,
    targetFrequencyBand: { min: 50, max: 150 },
    timingWindow: 0.05,
  },
  hiphop: {
    name: "Hip Hop",
    description: "Emphasize kicks and bass",
    frequencies: [80, 160, 320],
    onsetSensitivity: 0.6, // Was energyThreshold
    intensity: 0.9,
    duration: 70,
    icon: "🎧",
    latencyCompensation: 0,
    fftSize: 2048,
    hopSize: 512,
    targetFrequencyBand: { min: 40, max: 100 },
    timingWindow: 0.05,
  },
  jazz: {
    name: "Jazz",
    description: "Highlight brass and percussion",
    frequencies: [100, 200, 400],
    onsetSensitivity: 0.3, // Was energyThreshold
    intensity: 0.6,
    duration: 40,
    icon: "🎺",
    latencyCompensation: 0,
    fftSize: 2048,
    hopSize: 512,
    targetFrequencyBand: { min: 80, max: 250 },
    timingWindow: 0.05,
  },
  classical: {
    name: "Classical",
    description: "Enhance strings and woodwinds",
    frequencies: [40, 80, 160],
    onsetSensitivity: 0.2, // Was energyThreshold
    intensity: 0.5,
    duration: 60,
    icon: "🎻",
    latencyCompensation: 0,
    fftSize: 4096, // Larger FFT for better frequency resolution
    hopSize: 1024,
    targetFrequencyBand: { min: 100, max: 500 },
    timingWindow: 0.07, // Slightly larger window for more legato passages
  },
  speech: {
    name: "Speech",
    description: "Subtle feedback for vocal emphasis",
    frequencies: [200, 400, 800],
    onsetSensitivity: 0.1, // Was energyThreshold
    intensity: 0.3,
    duration: 30,
    icon: "🎙️",
    latencyCompensation: 0,
    fftSize: 1024, // Smaller FFT for speech
    hopSize: 256,
    targetFrequencyBand: { min: 150, max: 600 },
    timingWindow: 0.04,
  },
  acapella: {
    name: "Acapella",
    description: "Gentle vibrations for vocal harmonies",
    frequencies: [160, 320, 640],
    onsetSensitivity: 0.2, // Was energyThreshold
    intensity: 0.4,
    duration: 40,
    icon: "🗣️",
    latencyCompensation: 0,
    fftSize: 2048,
    hopSize: 512,
    targetFrequencyBand: { min: 100, max: 400 },
    timingWindow: 0.05,
  },
};
